import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { API, authFetch, useAuth } from "@/App";
import { toast } from "sonner";
import { 
  Building2, 
  FileText, 
  Shield, 
  TrendingUp, 
  CheckCircle2, 
  Download,
  BarChart3,
  Clock,
  Loader2,
  LogOut,
  ClipboardList,
  DollarSign,
  Target,
  AlertCircle
} from 'lucide-react';

const SponsorshipDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [engagementTiers, setEngagementTiers] = useState({});
  const [revenueRanges, setRevenueRanges] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [intakeForm, setIntakeForm] = useState({
    intended_engagement: '90_day',
    business_name: '',
    owner_name: '',
    industry: '',
    years_in_operation: '',
    revenue_range: '',
    growth_goal_percent: 20,
    existing_loan: false,
    target_financial_institution: '',
    primary_risk_area: ''
  });

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [appRes, tiersRes, rangesRes] = await Promise.all([
        authFetch(`${API}/sponsorship/application/${user.user_id}`),
        fetch(`${API}/sponsorship/engagement-tiers`),
        fetch(`${API}/sponsorship/revenue-ranges`)
      ]);

      if (appRes.ok) {
        const appData = await appRes.json();
        setApplication(appData);
        
        // Pre-fill form if intake exists
        if (appData.intake_data) {
          setIntakeForm(prev => ({
            ...prev,
            ...appData.intake_data,
            owner_name: appData.intake_data.owner_name || user?.name || ''
          }));
        } else {
          setIntakeForm(prev => ({
            ...prev,
            owner_name: user?.name || '',
            business_name: user?.business_name || ''
          }));
        }
      }

      if (tiersRes.ok) {
        const tiersData = await tiersRes.json();
        setEngagementTiers(tiersData.tiers || {});
      }

      if (rangesRes.ok) {
        const rangesData = await rangesRes.json();
        setRevenueRanges(rangesData.ranges || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitIntake = async () => {
    // Validation
    if (!intakeForm.business_name || !intakeForm.owner_name || !intakeForm.industry || 
        !intakeForm.years_in_operation || !intakeForm.revenue_range || !intakeForm.primary_risk_area) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const res = await authFetch(`${API}/sponsorship/intake?user_id=${user.user_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(intakeForm)
      });

      if (res.ok) {
        toast.success("Intake form submitted successfully!");
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to submit intake");
      }
    } catch (error) {
      toast.error("An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPacket = async () => {
    setDownloading(true);
    try {
      const res = await authFetch(`${API}/sponsorship/generate-packet/${user.user_id}`);
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Sponsorship_Packet_${intakeForm.business_name.replace(/\s+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Packet downloaded successfully!");
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to generate packet");
      }
    } catch (error) {
      toast.error("Error downloading packet");
    } finally {
      setDownloading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getStatusProgress = () => {
    if (!application) return 0;
    switch (application.status) {
      case 'intake_pending': return 25;
      case 'intake_complete': return 50;
      case 'packet_generated': return 75;
      case 'enrolled': return 100;
      default: return 0;
    }
  };

  const getStatusLabel = () => {
    if (!application) return 'Unknown';
    switch (application.status) {
      case 'intake_pending': return 'Intake Pending';
      case 'intake_complete': return 'Ready to Generate';
      case 'packet_generated': return 'Packet Generated';
      case 'enrolled': return 'Enrolled';
      default: return application.status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-neutral-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No Application Found</h2>
            <p className="text-neutral-600 mb-4">
              You don't have an active sponsorship application. Please purchase the Funding Readiness Packet to get started.
            </p>
            <Button onClick={() => navigate('/sponsorship')} className="bg-emerald-600 hover:bg-emerald-700">
              Purchase Packet
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="/mbc-logo.png" 
                alt="My Biz Consulting" 
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-lg font-semibold text-neutral-900">Sponsorship Gateway</h1>
                <p className="text-sm text-neutral-500">Funding Readiness Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-neutral-600 hidden sm:inline">{user?.email}</span>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Status Overview */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">Application Status</h2>
                <p className="text-sm text-neutral-500">
                  {getStatusLabel()}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-neutral-500">Enrollment Credit</p>
                <p className="text-2xl font-bold text-emerald-600">${application.enrollment_credit || 200}</p>
              </div>
            </div>
            <Progress value={getStatusProgress()} className="h-2" />
            <div className="flex justify-between mt-2 text-xs text-neutral-500">
              <span>Purchase</span>
              <span>Intake</span>
              <span>Generate</span>
              <span>Enroll</span>
            </div>
          </CardContent>
        </Card>

        {/* Dashboard Tabs */}
        <Tabs defaultValue={application.status === 'intake_pending' ? 'intake' : 'status'} className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="status" className="flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Status</span>
            </TabsTrigger>
            <TabsTrigger value="intake" className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              <span className="hidden sm:inline">Intake</span>
            </TabsTrigger>
            <TabsTrigger value="roi" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">ROI</span>
            </TabsTrigger>
            <TabsTrigger value="download" className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Download</span>
            </TabsTrigger>
          </TabsList>

          {/* Status Tab */}
          <TabsContent value="status">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-emerald-600" />
                    Sponsorship Status
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-neutral-600">Application ID</span>
                    <span className="font-mono text-sm">{application.application_id}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-neutral-600">Status</span>
                    <span className={`px-2 py-1 rounded text-sm font-medium ${
                      application.status === 'packet_generated' ? 'bg-emerald-100 text-emerald-700' :
                      application.status === 'intake_complete' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {getStatusLabel()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-neutral-600">Amount Paid</span>
                    <span className="font-semibold">${application.amount_paid}</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-neutral-600">Enrollment Credit</span>
                    <span className="font-semibold text-emerald-600">${application.enrollment_credit}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-emerald-600" />
                    Next Steps
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className={`flex items-start gap-3 p-3 rounded-lg ${
                      application.status === 'intake_pending' ? 'bg-amber-50 border border-amber-200' : 'bg-neutral-50'
                    }`}>
                      <CheckCircle2 className={`w-5 h-5 mt-0.5 ${
                        application.status !== 'intake_pending' ? 'text-emerald-600' : 'text-neutral-400'
                      }`} />
                      <div>
                        <p className="font-medium">Complete Intake Form</p>
                        <p className="text-sm text-neutral-500">Provide business details for your packet</p>
                      </div>
                    </div>
                    <div className={`flex items-start gap-3 p-3 rounded-lg ${
                      application.status === 'intake_complete' ? 'bg-amber-50 border border-amber-200' : 'bg-neutral-50'
                    }`}>
                      <CheckCircle2 className={`w-5 h-5 mt-0.5 ${
                        ['packet_generated', 'enrolled'].includes(application.status) ? 'text-emerald-600' : 'text-neutral-400'
                      }`} />
                      <div>
                        <p className="font-medium">Generate & Download Packet</p>
                        <p className="text-sm text-neutral-500">Download your institutional documentation</p>
                      </div>
                    </div>
                    <div className={`flex items-start gap-3 p-3 rounded-lg ${
                      application.status === 'packet_generated' ? 'bg-amber-50 border border-amber-200' : 'bg-neutral-50'
                    }`}>
                      <CheckCircle2 className={`w-5 h-5 mt-0.5 ${
                        application.status === 'enrolled' ? 'text-emerald-600' : 'text-neutral-400'
                      }`} />
                      <div>
                        <p className="font-medium">Secure Funding & Enroll</p>
                        <p className="text-sm text-neutral-500">Submit to institutions and enroll when ready</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Intake Form Tab */}
          <TabsContent value="intake">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-emerald-600" />
                  Funding Readiness Intake Form
                </CardTitle>
                <CardDescription>
                  Complete this form to generate your institutional sponsorship packet
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Intended Engagement */}
                  <div className="md:col-span-2">
                    <Label className="text-base font-semibold mb-3 block">Intended Engagement Program</Label>
                    <RadioGroup 
                      value={intakeForm.intended_engagement}
                      onValueChange={(value) => setIntakeForm(prev => ({ ...prev, intended_engagement: value }))}
                      className="grid md:grid-cols-2 gap-4"
                    >
                      <div className={`border rounded-lg p-4 cursor-pointer ${
                        intakeForm.intended_engagement === '90_day' ? 'border-emerald-500 bg-emerald-50' : 'border-neutral-200'
                      }`}>
                        <RadioGroupItem value="90_day" id="90_day" className="sr-only" />
                        <label htmlFor="90_day" className="cursor-pointer">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-semibold">90-Day Engagement</span>
                            <span className="text-emerald-600 font-bold">$1,500</span>
                          </div>
                          <p className="text-sm text-neutral-600">Stabilization & structured implementation support</p>
                        </label>
                      </div>
                      <div className={`border rounded-lg p-4 cursor-pointer ${
                        intakeForm.intended_engagement === '6_month' ? 'border-emerald-500 bg-emerald-50' : 'border-neutral-200'
                      }`}>
                        <RadioGroupItem value="6_month" id="6_month" className="sr-only" />
                        <label htmlFor="6_month" className="cursor-pointer">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-semibold">6-Month Engagement</span>
                            <span className="text-emerald-600 font-bold">$2,500</span>
                          </div>
                          <p className="text-sm text-neutral-600">Extended performance oversight with priority access</p>
                        </label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Business Info */}
                  <div>
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      value={intakeForm.business_name}
                      onChange={(e) => setIntakeForm(prev => ({ ...prev, business_name: e.target.value }))}
                      placeholder="Your Business LLC"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner_name">Owner Name *</Label>
                    <Input
                      id="owner_name"
                      value={intakeForm.owner_name}
                      onChange={(e) => setIntakeForm(prev => ({ ...prev, owner_name: e.target.value }))}
                      placeholder="Full legal name"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="industry">Industry *</Label>
                    <Input
                      id="industry"
                      value={intakeForm.industry}
                      onChange={(e) => setIntakeForm(prev => ({ ...prev, industry: e.target.value }))}
                      placeholder="e.g., Retail, Professional Services"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="years_in_operation">Years in Operation *</Label>
                    <Select 
                      value={intakeForm.years_in_operation}
                      onValueChange={(value) => setIntakeForm(prev => ({ ...prev, years_in_operation: value }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select years" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Less than 1 year">Less than 1 year</SelectItem>
                        <SelectItem value="1-2 years">1-2 years</SelectItem>
                        <SelectItem value="3-5 years">3-5 years</SelectItem>
                        <SelectItem value="5-10 years">5-10 years</SelectItem>
                        <SelectItem value="10+ years">10+ years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="revenue_range">Current Revenue Range *</Label>
                    <Select 
                      value={intakeForm.revenue_range}
                      onValueChange={(value) => setIntakeForm(prev => ({ ...prev, revenue_range: value }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select revenue range" />
                      </SelectTrigger>
                      <SelectContent>
                        {revenueRanges.map((range) => (
                          <SelectItem key={range.label} value={range.label}>
                            {range.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="growth_goal">Growth Goal % (6-12 months) *</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <Input
                        id="growth_goal"
                        type="number"
                        min="6"
                        max="100"
                        value={intakeForm.growth_goal_percent}
                        onChange={(e) => setIntakeForm(prev => ({ ...prev, growth_goal_percent: parseInt(e.target.value) || 20 }))}
                        className="w-24"
                      />
                      <span className="text-neutral-600">%</span>
                    </div>
                  </div>
                  <div>
                    <Label>Existing Business Loan? *</Label>
                    <RadioGroup 
                      value={intakeForm.existing_loan ? "yes" : "no"}
                      onValueChange={(value) => setIntakeForm(prev => ({ ...prev, existing_loan: value === "yes" }))}
                      className="flex gap-4 mt-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="loan_yes" />
                        <Label htmlFor="loan_yes">Yes</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="loan_no" />
                        <Label htmlFor="loan_no">No</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  <div>
                    <Label htmlFor="target_institution">Target Financial Institution (Optional)</Label>
                    <Input
                      id="target_institution"
                      value={intakeForm.target_financial_institution}
                      onChange={(e) => setIntakeForm(prev => ({ ...prev, target_financial_institution: e.target.value }))}
                      placeholder="Bank or institution name"
                      className="mt-1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="primary_risk">Primary Risk Area *</Label>
                    <Textarea
                      id="primary_risk"
                      value={intakeForm.primary_risk_area}
                      onChange={(e) => setIntakeForm(prev => ({ ...prev, primary_risk_area: e.target.value }))}
                      placeholder="Describe your primary business risk or challenge (e.g., cash flow management, scaling operations, market competition)"
                      className="mt-1 min-h-24"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button 
                    onClick={handleSubmitIntake}
                    disabled={submitting}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Submit Intake Form
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ROI Projection Tab */}
          <TabsContent value="roi">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-600" />
                  ROI Projection
                </CardTitle>
                <CardDescription>
                  Projected return on your engagement investment
                </CardDescription>
              </CardHeader>
              <CardContent>
                {application.intake_data ? (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-neutral-50 rounded-lg p-4">
                        <p className="text-sm text-neutral-500 mb-1">Current Revenue (Midpoint)</p>
                        <p className="text-2xl font-bold text-neutral-900">
                          ${(application.intake_data.revenue_midpoint || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-neutral-50 rounded-lg p-4">
                        <p className="text-sm text-neutral-500 mb-1">Growth Target</p>
                        <p className="text-2xl font-bold text-neutral-900">
                          {application.intake_data.growth_goal_percent}%
                        </p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-4">
                        <p className="text-sm text-emerald-600 mb-1">Projected Increase</p>
                        <p className="text-2xl font-bold text-emerald-700">
                          ${(application.intake_data.projected_increase || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-emerald-50 rounded-lg p-4">
                        <p className="text-sm text-emerald-600 mb-1">Projected New Revenue</p>
                        <p className="text-2xl font-bold text-emerald-700">
                          ${(application.intake_data.projected_new_revenue || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div className="bg-neutral-50 rounded-lg p-6">
                      <h4 className="font-semibold mb-4">Projected Impact Summary</h4>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-2">
                          <TrendingUp className="w-5 h-5 text-emerald-600 mt-0.5" />
                          <div>
                            <p className="font-medium">Estimated 6–12 month revenue lift</p>
                            <p className="text-sm text-neutral-600">
                              ${(application.intake_data.projected_increase || 0).toLocaleString()}
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start gap-2">
                          <Shield className="w-5 h-5 text-emerald-600 mt-0.5" />
                          <div>
                            <p className="font-medium">Operational stabilization through structured oversight</p>
                            <p className="text-sm text-neutral-600">Consultant-led implementation with milestone tracking</p>
                          </div>
                        </li>
                        <li className="flex items-start gap-2">
                          <DollarSign className="w-5 h-5 text-emerald-600 mt-0.5" />
                          <div>
                            <p className="font-medium">Improved capital efficiency</p>
                            <p className="text-sm text-neutral-600">Better resource allocation and financial visibility</p>
                          </div>
                        </li>
                      </ul>
                    </div>

                    <p className="text-xs text-neutral-500 text-center">
                      Projections are performance-based estimates and depend on execution, market conditions, and external variables.
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <BarChart3 className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                    <p className="text-neutral-600">Complete the intake form to see your ROI projection</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Download Tab */}
          <TabsContent value="download">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-emerald-600" />
                  Download Institutional Packet
                </CardTitle>
                <CardDescription>
                  Generate and download your sponsorship documentation
                </CardDescription>
              </CardHeader>
              <CardContent>
                {['intake_complete', 'packet_generated', 'enrolled'].includes(application.status) ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Your Packet is Ready</h3>
                    <p className="text-neutral-600 mb-6 max-w-md mx-auto">
                      Download your institutional-grade sponsorship packet including engagement overview, 
                      risk mitigation framework, ROI projections, and sponsorship letter template.
                    </p>
                    <Button 
                      onClick={handleDownloadPacket}
                      disabled={downloading}
                      size="lg"
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {downloading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Generating PDF...
                        </>
                      ) : (
                        <>
                          <Download className="w-5 h-5 mr-2" />
                          Download Sponsorship Packet
                        </>
                      )}
                    </Button>
                    {application.packet_generated_at && (
                      <p className="text-sm text-neutral-500 mt-4">
                        Last generated: {new Date(application.packet_generated_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <ClipboardList className="w-8 h-8 text-amber-600" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Complete Intake First</h3>
                    <p className="text-neutral-600 mb-4">
                      Please complete the intake form before generating your sponsorship packet.
                    </p>
                    <Button 
                      variant="outline"
                      onClick={() => document.querySelector('[value="intake"]')?.click()}
                    >
                      Go to Intake Form
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default SponsorshipDashboard;
